import type React from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { deleteCookie, getCookie } from 'cookies-next/client';
import { sha256 } from 'js-sha256';

import { getPersistentId } from '../activityTracker/helpers';

import { revokeAccessToken } from './api';

import {
  ACCESS_TOKEN_KEY,
  EXTERNAL_ID_KEY,
  HASHED_EXTERNAL_ID_KEY,
  HASHED_USER_EMAIL_KEY,
  USER_ID_KEY,
} from '@/constants/cookies';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useConsentManager } from '@/modules/consent/hooks/useConsentManager';
import { segmentEventTypes } from '@/modules/externalTracking/types';
import { useExternalTracking } from '@/modules/externalTracking/useExternalTracking';
import { reloadPage } from '@/modules/routing/helpers';
import { useCurrentUser } from '@/modules/user/useCurrentUser';
import { deleteGTMCookies, setGTMCookies } from '@/utils/setExternalIdCookie';

export function useAuth() {
  const { branch, gtm, segment } = useExternalTracking();
  const { sendActivityTrackerEvent } = useActivityTracker();
  const [, user] = useCurrentUser();
  const { airgap } = useConsentManager();

  const logoutUserEvent = {
    category: 'Logout',
    action: 'user clicks logout',
  };

  async function logout(e?: React.MouseEvent<HTMLButtonElement> | Event) {
    /**
     * Fire and forget request to revoke access token
     * it is not critical that this succeeds, just a nice-to-have
     */
    revokeAccessToken(getCookie(ACCESS_TOKEN_KEY) || '');

    gtm.sendEvent(logoutUserEvent);
    sendActivityTrackerEvent(ActivityTrackerEventType.LOGOUT_ACTION);

    branch.logout();

    if (user) {
      segment.sendEvent(segmentEventTypes.SIGNED_OUT, {
        userId: user.id.toString(),
        username: user.username,
      });
    }

    deleteCookie(ACCESS_TOKEN_KEY, { path: '/' });
    deleteGTMCookies({
      cookieKeyList: [USER_ID_KEY, HASHED_USER_EMAIL_KEY],
    });
    const persistentId = await getPersistentId();
    if (persistentId) {
      const hashedPersistentId = sha256(persistentId);
      setGTMCookies({
        cookieRecord: {
          [EXTERNAL_ID_KEY]: persistentId,
          [HASHED_EXTERNAL_ID_KEY]: hashedPersistentId,
        },
      });
    }

    datadogLogs.clearUser();
    datadogRum.clearUser();

    // we have to send a trusted event to airgap functions that change consent
    // that means a real event that was triggered in the ui by a user action.
    // we do that here and pass it into the function
    // part 2:
    // in production react releases the event fairly quickly and the way we have the
    // desktop binding setup we lose it by the time we get here. So I'm passing the
    // native event from the handler (see line 106 of src/components/ActionMenu/index.tsx)
    // but in the mobile menu I pass the event directly so we have the code running in line 73
    // below and the checks at lines 75 and 79.
    if (e) {
      const { nativeEvent } = e as React.MouseEvent<HTMLButtonElement>;
      await airgap?.reset(nativeEvent || (e as Event), true);
      const newConsent = airgap?.getConsent();
      if (newConsent && newConsent.purposes) {
        await airgap?.setConsent(
          nativeEvent || (e as Event),
          newConsent.purposes,
          {
            prompted: false,
            confirmed: false,
          }
        );
      }
    }

    reloadPage();
  }

  return { logout };
}
